import {
    LockClosedIcon,
    Pencil1Icon,
    UpdateIcon,
} from '@radix-ui/react-icons';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from '../ui/dropdown-menu';
import {
    Form,
    FormControl, FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '../ui/form';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';
import { Textarea } from '../ui/textarea';

function FinishClassDialog({ competitionClass, fetchClass, dropdown }) {
    const form = useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [formData, setFormData] = useState({
        comment: competitionClass?.comment || '',
        firstTimekeeper: competitionClass?.time_keeper_1 || '',
        secondTimekeeper: competitionClass?.time_keeper_2 || '',
    });

    const handleChange = async (e) => {
        e.preventDefault();
        if (isLoading) return;

        // Validate that both timekeepers are provided
        if (!formData.firstTimekeeper || !formData.secondTimekeeper) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: 'Båda tidtagarna måste anges.',
            });
            return;
        }

        setIsLoading(true);
        setError(null);

        const { behaviour } = competitionClass.finished_at
            ? { behaviour: 'edit-class-notes' }
            : { behaviour: 'finish-class' };

        try {
            let editedClass = competitionClass.finished_at;
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/judges/${behaviour}`, {
                sportClassId: competitionClass.id,
                ...formData,
            });
            fetchClass();
            if (editedClass) {
                toast({
                    title: 'Kommentarer uppdaterade.',
                    description: `${competitionClass.name} uppdaterades. ${formData.comment}, ${formData.firstTimekeeper}, ${formData.secondTimekeeper}`,
                });
            } else {
                toast({
                    title: 'Klass avslutad.',
                    description: `${competitionClass.name} blev avslutad. ${formData.comment}`,
                });
            }

            setIsDialogOpen(false);
        } catch (error) {
            setError(error?.response?.data?.error);
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            {dropdown ? (
                <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                    <DialogTrigger className={`flex items-center w-full ${!competitionClass.finished_at && 'text-destructive'}`}>
                        {competitionClass.finished_at ?
                            <><Pencil1Icon className='mr-3' />Ändra avslut</>
                            :
                            <><LockClosedIcon className='mr-3' />Avsluta klass</>
                        }
                    </DialogTrigger>
                </DropdownMenuItem>
            ) : (
                <DialogTrigger className='flex items-center w-full md:col-start-2' asChild>
                    <Button variant={`${!competitionClass.finished_at && 'text-destructive'}`}>
                        {competitionClass.finished_at ?
                            <><Pencil1Icon className='mr-3' />Ändra avslut</>
                            :
                            <><LockClosedIcon className='mr-3' />Avsluta klass</>
                        }
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>{competitionClass.finished_at ? 'Ändra avslut för' : 'Avsluta'} {competitionClass.name}</DialogTitle>
                    <DialogDescription>{competitionClass.finished_at ? 'Uppdatera avslutande kommentar eller tidtagare.' : 'Är du helt säker? Denna åtgärd går inte att ångra.'}</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleChange} className="flex flex-col gap-6">
                        <FormField
                            control={form.control}
                            name="comment"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='comment'>Kommentar</FormLabel>
                                    <FormControl>
                                        {/* <Input
                                            id='comment'
                                            type='text'
                                            placeholder=''
                                            value={formData.comment}
                                            onChange={(e) => setFormData(prevState => ({ ...prevState, comment: e.target.value }))}
                                            autoComplete='off'
                                        /> */}
                                        <Textarea
                                            id='comment'
                                            value={formData.comment}
                                            onChange={(e) => setFormData(prevState => ({ ...prevState, comment: e.target.value }))}
                                            autoComplete='off'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="firstTimekeeper"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='firstTimekeeper'>Förste tidtagare</FormLabel>
                                    <FormControl>
                                        <Input
                                            id='firstTimekeeper'
                                            type='text'
                                            placeholder=''
                                            value={formData.firstTimekeeper}
                                            onChange={(e) => setFormData(prevState => ({ ...prevState, firstTimekeeper: e.target.value }))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="secondTimekeeper"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='secondTimekeeper'>Andre tidtagare</FormLabel>
                                    <FormControl>
                                        <Input
                                            id='secondTimekeeper'
                                            type='text'
                                            placeholder=''
                                            value={formData.secondTimekeeper}
                                            onChange={(e) => setFormData(prevState => ({ ...prevState, secondTimekeeper: e.target.value }))}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button
                                onClick={handleChange}
                                disabled={isLoading}>
                                {(isLoading) ? <UpdateIcon className='loader-spinner' /> : <>{competitionClass.finished_at ? 'Uppdatera' : 'Avsluta klass'}</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default FinishClassDialog;
